/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import {
  HUMaterialTable,
  HUMaterialTableProps,
  iconCellStyles,
} from '../../components/HUMaterialTable/HUMaterialTable';
import { Namespaces } from '../../i18n';
import { AdminOrganization } from '../../types/superuser/AdminOrganization';
import { adminFetchOrganizations } from '../../utils/queryFns';
import { truncate } from '../../utils/truncate';
import { useAdminOrganizationsPageStyles } from './AdminOrganizationsPage.styles';
import { AdminOrganizationDetailsPanel } from './components/AdminOrganizationDetailsPanel/AdminOrganizationDetailsPanel';
import { AdminOrganizationInfoList } from './components/AdminOrganizationInfoList/AdminOrganizationInfoList';

export function AdminOrganizationsPage() {
  const classes = useAdminOrganizationsPageStyles();
  const { t } = useTranslation(Namespaces.Admin);
  const queryOrganizations = useQuery(adminFetchOrganizations.key, adminFetchOrganizations.fn, {
    refetchOnWindowFocus: false,
  });

  const organizations = useMemo(() => queryOrganizations.data?.payload ?? [], [queryOrganizations]);

  const isOrganizationsLoading = useMemo(() => queryOrganizations.status === 'loading', [queryOrganizations.status]);

  const isError = useMemo(
    () => queryOrganizations.error && queryOrganizations.status === 'error',
    [queryOrganizations.error, queryOrganizations.status],
  );

  const getStatusIcon = useCallback(
    (status: boolean | undefined) =>
      status ? <CheckCircleIcon className={classes.success} /> : <CancelIcon className={classes.failure} />,
    [classes.success, classes.failure],
  );

  const tableConfig = useMemo<HUMaterialTableProps<AdminOrganization>>(
    () => ({
      columns: [
        {
          title: t('organizations.table.label.name'),
          field: 'name',
        },
        {
          title: t('organizations.table.label.email'),
          field: 'email',
          render: rowData => truncate(rowData.email || '-', 35, '...'),
        },
        {
          title: t('organizations.table.label.description'),
          field: 'description',
          render: rowData => truncate(rowData.description || '-', 35, '...'),
        },
        {
          title: t('organizations.table.label.company_name'),
          field: 'companyName',
        },
        {
          title: t('organizations.table.label.verified'),
          field: 'verified',
          render: rowData => getStatusIcon(rowData.verified),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
      ],
      title: t('organizations.title'),
      data: organizations,
      detailPanel: [
        {
          tooltip: t('organizations.table.label.info'),
          icon: InfoIcon,
          render: ({ rowData }) => <AdminOrganizationDetailsPanel rowData={rowData} />,
        },
        {
          tooltip: t('organizations.table.label.show_more'),
          render: ({ rowData }) => <AdminOrganizationInfoList rowData={rowData} />,
        },
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
    }),
    [getStatusIcon, organizations, t],
  );

  return isOrganizationsLoading ? (
    <AlignCenter>
      <CircularProgress color="primary" size={40} />
    </AlignCenter>
  ) : isError ? (
    <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
  ) : (
    <HUMaterialTable {...tableConfig} />
  );
}
